import React, { useState } from 'react'
import Tabs from '@mui/joy/Tabs'
import TabList from '@mui/joy/TabList'
import Tab from '@mui/joy/Tab'
import TabPanel from '@mui/joy/TabPanel'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Badge from '@mui/joy/Badge'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import { useTranslation, Trans } from 'react-i18next'

import GarageList from './garage-list'
import GarageMap from './garage-map'
import Filters from './filters'
import FullGarageToggleIsolated from './full-garage-toggle-isolated'
import { getCurrency } from '../helpers'
import { useGarageView } from '../hooks/use-garage-view'

const currency = getCurrency()

export default function GarageView() {

    const [tab, setTab] = useState<0 | 1 | 2>(0)

    const { t } = useTranslation()
    
    const {
        initialFilters,
        currentFilters,
        setCurrentFilters,
        priceRange,
        heightRange,
        widthRange,
        lengthRange,
        weightRange,
        desktopFilters,
        selectedId,
        setSelectedId,
        hoverPoint,
        setHoverPoint,
        searchLocationPoint,
        sortedFilteredGarageList,
        loadingGarageList,
        loadingGarageAvailability,
        garagesAvailableLater,
        errors,
        handleFiltersReset,
        handleTabChange,
        handleToggleDesktopFilters,
        shallowEqual,
    } = useGarageView(currency, setTab)

    return (
        <>
            <Tabs
                aria-label="Basic tabs"
                value={tab}
                onChange={handleTabChange}
                sx={{ 
                    height: '100%',
                    flexGrow: 1,
                    display: {
                        md: 'none',
                    }
                }}
            >
                <TabPanel value={0} sx={{ 
                    p: 1,
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: '0px',
                    overflowY: 'auto',
                    backgroundColor: '#f7f7f7',
                }}>
                    <GarageList
                        garageList={sortedFilteredGarageList}
                        loadingGarageList={loadingGarageList}
                        loadingGarageAvailability={loadingGarageAvailability}
                        garagesAvailableLater={garagesAvailableLater}
                        errors={errors}
                        currency={currency}
                    />
                </TabPanel>
                <TabPanel value={1} sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }} keepMounted>
                    {!loadingGarageList &&
                        <GarageMap
                            garageList={sortedFilteredGarageList}
                            isActive={tab === 1}
                            selectedId={selectedId}
                            setSelectedId={setSelectedId}
                            hoverPoint={hoverPoint}
                            setHoverPoint={setHoverPoint}
                            searchLocationPoint={searchLocationPoint}
                            loadingGarageAvailability={loadingGarageAvailability}
                            garagesAvailableLater={garagesAvailableLater}
                            currency={currency}
                            navigateToGarage={null}
                        />
                    }
                </TabPanel>
                <TabPanel value={2} sx={{
                    p: 0,
                    paddingBottom: 10,
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: '0px',
                    overflowY: 'auto',
                    backgroundColor: '#f7f7f7',
                }}>
                    <Filters
                        currentFilters={currentFilters}
                        setCurrentFilters={setCurrentFilters}
                        results={sortedFilteredGarageList.length}
                        handleFiltersReset={handleFiltersReset}
                        handleTabChange={handleTabChange}
                        priceRange={priceRange}
                        heightRange={heightRange}
                        widthRange={widthRange}
                        lengthRange={lengthRange}
                        weightRange={weightRange}
                        currency={currency}
                        desktop={false}
                        handleToggleDesktopFilters={handleToggleDesktopFilters}
                    />
                </TabPanel>
                <TabList 
                    variant="outlined"
                    disableUnderline
                    sx={{
                        position: 'fixed',
                        width: 'calc(100vw - 2rem)',
                        bottom: '1rem',
                        right: '1rem',
                        zIndex: 10,
                        borderRadius: '0.25rem',
                        backgroundColor: 'white',
                        p: 0.75,
                        border: '1px solid var(--joy-palette-primary-300)',
                        boxShadow: tab !== 2 ? '0px 15px 25px 30px rgba(60, 60, 60, 0.4)' : 'none',
                    }}
                >
                    <Tab
                        variant={tab === 0 ? 'solid' : 'plain'}
                        // @ts-ignore - custom theme color
                        color={tab === 0 ? 'secondary' : 'neutral'}
                        disableIndicator
                        sx={{
                            flex: '0 0 33.33%',
                            margin: 0,
                            color: 'black',
                            borderRadius: '0.25rem',
                            "&:hover": {
                                color: 'black',
                                fontWeight: 500,
                            },
                        }}
                    >
                        <Box fontWeight={600} fontSize="0.875rem">{t('general.list')}</Box>
                    </Tab>
                    <Tab
                        variant={tab === 1 ? 'solid' : 'plain'}
                        // @ts-ignore - custom theme color
                        color={tab === 1 ? 'secondary' : 'neutral'}
                        disableIndicator
                        sx={{
                            flex: '0 0 33.33%',
                            margin: 0,
                            color: 'black',
                            borderRadius: '0.25rem',
                            "&:hover": {
                                color: 'black',
                                fontWeight: 500,
                            },
                        }}
                    >
                        <Box fontWeight={600} fontSize="0.875rem">{t('general.map')}</Box>
                    </Tab>
                    <Tab
                        variant={tab === 2 ? 'solid' : 'plain'}
                        // @ts-ignore - custom theme color
                        color={tab === 2 ? 'secondary' : 'neutral'}
                        disableIndicator
                        sx={{
                            flex: '0 0 33.33%',
                            margin: 0,
                            color: 'black',
                            borderRadius: '0.25rem',
                            "&:hover": {
                                color: 'black',
                            },
                        }}
                    >
                        <Badge
                            badgeContent={shallowEqual(currentFilters, initialFilters)}
                        >
                            <Box px={1.5} fontWeight={600} fontSize="0.875rem">{t('general.filters')}</Box>
                        </Badge>
                    </Tab>
                </TabList>
            </Tabs>
            <Box sx={{
                height: '100%',
                flexGrow: 1,
                display: {
                    xs: 'none',
                    md: 'flex',
                }
            }}>
                <Box sx={{
                    width: '30rem',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" flexDirection="column">
                            <Typography level='body-md' flexGrow={1}>
                                <Trans i18nKey="general.garagesFound" count={sortedFilteredGarageList.length} />
                            </Typography>
                            <FullGarageToggleIsolated 
                                currentFilters={currentFilters}
                                setCurrentFilters={setCurrentFilters}
                            />
                        </Box>
                        <Badge
                            badgeContent={shallowEqual(currentFilters, initialFilters)}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleToggleDesktopFilters}
                            >{t('general.filters')}</Button>
                        </Badge>
                    </Box>
                    <Box sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: '0px',
                        overflowY: 'auto',
                        padding: 1,
                    }}>
                        <GarageList
                            garageList={sortedFilteredGarageList}
                            loadingGarageList={loadingGarageList}
                            loadingGarageAvailability={loadingGarageAvailability}
                            hoverPoint={hoverPoint}
                            setHoverPoint={setHoverPoint}
                            setSelectedId={setSelectedId}
                            garagesAvailableLater={garagesAvailableLater}
                            errors={errors}
                            currency={currency}
                        />
                    </Box>
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {!loadingGarageList &&
                        <GarageMap
                            garageList={sortedFilteredGarageList}
                            selectedId={selectedId}
                            setSelectedId={setSelectedId}
                            hoverPoint={hoverPoint}
                            setHoverPoint={setHoverPoint}
                            searchLocationPoint={searchLocationPoint}
                            loadingGarageAvailability={loadingGarageAvailability}
                            garagesAvailableLater={garagesAvailableLater}
                            currency={currency}
                            navigateToGarage={null}
                        />
                    }
                </Box>
                <Modal open={desktopFilters} onClose={() => handleToggleDesktopFilters()}>
                    <ModalDialog
                        aria-labelledby="modal-title"
                        variant="outlined"
                        sx={{
                            backgroundColor: '#f7f7f7',
                            minWidth: '30rem',
                            padding: 2,
                        }}
                    >
                        <ModalClose variant="outlined"/>
                        <Typography id="modal-title" component="h5" level="title-lg" sx={{
                            fontSize: '1.25rem',
                            px: 2,
                            pt: 1,
                        }}>
                            {t('general.filters')}
                        </Typography>
                        <Filters
                            currentFilters={currentFilters}
                            setCurrentFilters={setCurrentFilters}
                            results={sortedFilteredGarageList.length}
                            handleFiltersReset={handleFiltersReset}
                            handleTabChange={handleTabChange}
                            priceRange={priceRange}
                            heightRange={heightRange}
                            widthRange={widthRange}
                            lengthRange={lengthRange}
                            weightRange={weightRange}
                            currency={currency}
                            desktop={true}
                            handleToggleDesktopFilters={handleToggleDesktopFilters}
                        />
                    </ModalDialog>
                </Modal>
            </Box>
        </>
    )
}
